// Public parcels tab — Mapbox map + parcel table. Reads geometry from
// the public-share payload directly (no auth-only fetch) so the
// recipient gets the same map experience as the internal portal.

import { useMemo, useState } from "react"
import { ChevronDown, ChevronUp, MapPin } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Card } from "@/components/ui/card"
import { ParcelsNavOverlay } from "@/features/transit/components/traceability/parcels-nav"
import { TraceabilityParcelsMap } from "@/features/transit/components/traceability/parcels-map"
import type {
  OTTraceability,
  ParcelGeoEntry,
  TraceabilityParcelEntry,
} from "@/features/transit/api/types"

interface Props {
  traceability: OTTraceability
  parcelGeo: ParcelGeoEntry[]
}

export function TraceabilityParcelsTabPublic({ traceability, parcelGeo }: Props) {
  const { t } = useTranslation("transit")
  const [userSelectedId, setUserSelectedId] = useState<number | null>(null)
  const [listOpen, setListOpen] = useState(false)

  // Index the consolidated parcels for quick lookup; we lean on the
  // geo entries for ordering since they're the ones with polygons.
  const traceById = useMemo(() => {
    const map = new Map<number, TraceabilityParcelEntry>()
    for (const p of traceability.parcels) map.set(p.id, p)
    return map
  }, [traceability.parcels])

  // Render order: union of geo + consolidated, geo first (drawn on
  // map). Centroid + locations come from geo; compliance/region from
  // the consolidated payload.
  const rows = useMemo(() => {
    const seen = new Set<number>()
    const result: Array<{
      id: number
      parcelId: string | null
      internalId: string | null
      farmerId: number
      hectares: number
      status: string
      isAtRiskDeforested: boolean
      regionName: string | null
      isCompliant: boolean
    }> = []
    const trace = traceability.parcels
    const geoIds = new Set(parcelGeo.map((p) => p.id))
    for (const g of parcelGeo) {
      const c = traceById.get(g.id)
      seen.add(g.id)
      result.push({
        id: g.id,
        parcelId: g.parcelId ?? c?.parcelId ?? null,
        internalId: g.internalId ?? c?.internalId ?? null,
        farmerId: g.farmerId,
        hectares: c?.hectares ?? g.hectares,
        status: c?.status ?? g.status,
        isAtRiskDeforested:
          c?.isAtRiskDeforested ?? g.isAtRiskDeforested,
        regionName: c?.regionName ?? null,
        isCompliant: c?.isCompliant ?? !g.isAtRiskDeforested,
      })
    }
    for (const c of trace) {
      if (geoIds.has(c.id) || seen.has(c.id)) continue
      result.push({
        id: c.id,
        parcelId: c.parcelId,
        internalId: c.internalId,
        farmerId: c.farmerId,
        hectares: c.hectares,
        status: c.status,
        isAtRiskDeforested: c.isAtRiskDeforested,
        regionName: c.regionName,
        isCompliant: c.isCompliant,
      })
    }
    return result
  }, [parcelGeo, traceability.parcels, traceById])

  // Parcels with usable geometry — the nav strip and map both use this.
  const navParcels = useMemo(
    () =>
      parcelGeo.filter(
        (p) => Array.isArray(p.locations) && p.locations.length > 0,
      ),
    [parcelGeo],
  )

  // Effective selection: explicit pick falls back to the first parcel
  // with geometry. Derived at render — no sync effect needed.
  const selectedId =
    userSelectedId ?? (navParcels.length > 0 ? navParcels[0].id : null)

  const selectedIndex = useMemo(() => {
    if (selectedId == null) return 0
    const i = navParcels.findIndex((p) => p.id === selectedId)
    return i >= 0 ? i : 0
  }, [navParcels, selectedId])

  const goPrev = () => {
    if (navParcels.length === 0) return
    const next = (selectedIndex - 1 + navParcels.length) % navParcels.length
    setUserSelectedId(navParcels[next].id)
  }
  const goNext = () => {
    if (navParcels.length === 0) return
    const next = (selectedIndex + 1) % navParcels.length
    setUserSelectedId(navParcels[next].id)
  }

  return (
    <div className="flex flex-col gap-4">
      <TraceabilityParcelsMap
        parcels={parcelGeo}
        selectedId={selectedId}
        onSelect={(p) => setUserSelectedId(p.id)}
      >
        {navParcels.length > 0 ? (
          <ParcelsNavOverlay
            parcels={navParcels.map((p) => ({
              id: p.id,
              parcelId: p.parcelId,
              internalId: p.internalId,
            }))}
            selectedIndex={selectedIndex}
            onPrev={goPrev}
            onNext={goNext}
            listOpen={listOpen}
            onToggleList={() => setListOpen((v) => !v)}
          />
        ) : null}
      </TraceabilityParcelsMap>

      <Card className="overflow-hidden p-0">
        <button
          type="button"
          onClick={() => setListOpen((v) => !v)}
          className="flex w-full items-center justify-between gap-2 border-b px-4 py-3 text-left transition-colors hover:bg-muted/40"
          aria-expanded={listOpen}
        >
          <div className="flex items-center gap-2">
            <MapPin className="size-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold tracking-tight text-foreground">
              {t("trace.parcels.tableTitle", { count: rows.length })}
            </h2>
          </div>
          {listOpen ? (
            <ChevronUp className="size-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="size-4 text-muted-foreground" />
          )}
        </button>
        {listOpen ? (
          rows.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">
              {t("trace.parcels.empty")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-[10px] uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">
                      {t("trace.parcels.col.id")}
                    </th>
                    <th className="px-3 py-2 text-right font-semibold">
                      {t("trace.parcels.col.size")}
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      {t("trace.parcels.col.region")}
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      {t("trace.parcels.col.deforestation")}
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      {t("trace.parcels.col.status")}
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      {t("trace.parcels.col.compliance")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((parcel) => {
                    const isSelected = selectedId === parcel.id
                    return (
                      <tr
                        key={parcel.id}
                        className={`cursor-pointer hover:bg-muted/40 ${
                          isSelected ? "bg-amber-50 dark:bg-amber-500/10" : ""
                        }`}
                        onClick={() =>
                          setUserSelectedId(isSelected ? null : parcel.id)
                        }
                      >
                        <td className="px-3 py-2 font-medium text-foreground">
                          {parcel.parcelId ??
                            parcel.internalId ??
                            `#${parcel.id}`}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {parcel.hectares.toFixed(2)} ha
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {parcel.regionName ?? "—"}
                        </td>
                        <td className="px-3 py-2">
                          {parcel.isAtRiskDeforested ? (
                            <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-500/15 dark:text-red-300">
                              {t("trace.parcels.deforestKo")}
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                              {t("trace.parcels.deforestOk")}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {parcel.status}
                        </td>
                        <td className="px-3 py-2">
                          {parcel.isCompliant ? (
                            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                              {t("trace.parcels.statusOk")}
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-500/15 dark:text-red-300">
                              {t("trace.parcels.statusRisk")}
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : null}
      </Card>
    </div>
  )
}
